import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { Form } from 'react-final-form';
import moment from 'moment';
import DialogV2 from 'common/modal/DialogV2';
import useAuthorizationFormData from '../shared/useAuthorizationFormData';
import {
  DATE_TYPE_VALUES,
  validateAuthorizationForm,
  buildFormInitialValues,
  closeModal,
} from '../../../utils/serviceAuthorizationUtils';
import { buildSubmitPayload } from '../shared/buildSubmitPayload';
import AuthorizationSpendSection from './AuthorizationSpendSection';
import RequestDetailsSection from './RequestDetailsSection';
import AuthorizationDetailsSection from './AuthorizationDetailsSection';

const ServiceAuthorizationFormModal = ({
  authorization,
  modalRef,
  title,
  confirmLabel,
  description,
  showRequestDetails,
  dataTestId,
  confirmButtonTestId,
  initialValuesPrefix,
  extraInitialValues,
  showEditReason,
  onSubmit,
}) => {
  const {
    feeScheduleProgram,
    isCostBased,
    isUnitBased,
    isAmountExceeded,
    setIsAmountExceeded,
    totalInvoicedSpend,
    invoicedSpendRecords,
    isInvoicedSpendLoaded,
    initialClinicalModificationCodeValue,
  } = useAuthorizationFormData(authorization);

  const formInitialValues = useMemo(
    () => buildFormInitialValues(authorization, {
      prefix: initialValuesPrefix,
      isUnitBased,
      extraFields: {
        ...extraInitialValues,
        clinical_modification_code: initialClinicalModificationCodeValue,
      },
    }),
    [
      authorization,
      initialValuesPrefix,
      isUnitBased,
      extraInitialValues,
      initialClinicalModificationCodeValue,
    ],
  );

  const handleSubmit = async (formData, form) => {
    const payload = buildSubmitPayload(formData, { isUnitBased, prefix: 'approved' });
    await onSubmit({
      payer_authorization_number: formData.payer_authorization_number,
      reviewer_note: formData.reviewer_note,
      ...payload,
    });
    closeModal(modalRef, form.reset);
  };

  return (
    <Form
      keepDirtyOnReinitialize
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      validate={(values) => validateAuthorizationForm(values, {
        isAmountExceeded,
        isUnitBased,
        isInvoicedSpendLoaded,
        totalInvoicedSpend,
        invoicedSpendRecords,
        authorization,
      })}
      render={({
        error,
        form,
        handleSubmit: formHandleSubmit,
        valid,
        values: {
          authorized_amount,
          date_type,
          range_date_start,
          range_date_end,
          single_date,
        },
      }) => (
        <DialogV2
          ref={modalRef}
          title={title}
          confirmLabel={confirmLabel}
          cancelHandler={() => closeModal(modalRef, form.reset)}
          confirmationHandler={formHandleSubmit}
          confirmationBtnDisabled={!valid}
          dataTestId={dataTestId}
          confirmButtonTestId={confirmButtonTestId}
          width="3xl"
        >
          <p>{description}</p>
          {showRequestDetails && (
            <RequestDetailsSection
              authorization={authorization}
              isUnitBased={isUnitBased}
              feeScheduleProgram={feeScheduleProgram}
            />
          )}
          <AuthorizationSpendSection
            feeScheduleProgram={feeScheduleProgram}
            person={authorization?.case?.person}
            isUnitBased={isUnitBased}
            requestedStartsAt={
              moment.unix(
                date_type === DATE_TYPE_VALUES.RANGE_DATE ?
                  range_date_start :
                  single_date,
              ).utc()
            }
            requestedEndsAt={
              moment.unix(
                date_type === DATE_TYPE_VALUES.RANGE_DATE ?
                  (range_date_end || range_date_start) :
                  single_date,
              ).utc()
            }
            authorizedAmount={authorized_amount}
            setIsAmountExceeded={setIsAmountExceeded}
            authorization={authorization}
          />
          <AuthorizationDetailsSection
            dateType={date_type}
            error={error}
            isUnitBased={isUnitBased}
            isCostBased={isCostBased}
            feeScheduleProgram={feeScheduleProgram}
            initialClinicalModificationCodeValue={initialClinicalModificationCodeValue}
            showEditReason={showEditReason}
          />
        </DialogV2>
      )}
    />
  );
};

ServiceAuthorizationFormModal.defaultProps = {
  showRequestDetails: false,
  description: '',
  extraInitialValues: {},
  showEditReason: false,
};

ServiceAuthorizationFormModal.propTypes = {
  authorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    original_service_authorization: PropTypes.string,
    fee_schedule_program: PropTypes.shape({
      fee_schedule: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }).isRequired,
    requested_cents: PropTypes.number,
    requested_starts_at: PropTypes.string.isRequired,
    requested_ends_at: PropTypes.string.isRequired,
    requested_unit_amount: PropTypes.number,
    approved_cents: PropTypes.number,
    approved_starts_at: PropTypes.string,
    approved_ends_at: PropTypes.string,
    approved_unit_amount: PropTypes.number,
    short_id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    case: PropTypes.shape({
      person: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }),
    clinical_modifications: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  modalRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  title: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  description: PropTypes.node,
  showRequestDetails: PropTypes.bool,
  dataTestId: PropTypes.string.isRequired,
  confirmButtonTestId: PropTypes.string.isRequired,
  initialValuesPrefix: PropTypes.oneOf(['approved', 'requested']).isRequired,
  extraInitialValues: PropTypes.object,
  showEditReason: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

export default ServiceAuthorizationFormModal;
